import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Clock, Eye, EyeSlash, Trash } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { ConfirmDialog, FloatingTextField, SefanoButton } from "@/components/Molecules";
import type { SettingsDangerProps } from "@/components/Templates/Settings/Settings.template.types";
import * as styles from "./SettingsDangerPanel.styles";

type SettingsDangerPanelProps = SettingsDangerProps;

export function SettingsDangerPanel({
  form,
  onSubmit,
  shopName,
  isShopActive,
  deleteRequested,
  scheduledDeleteLabel,
  deleteMatches,
  isToggling,
  isSchedulingDelete,
  isCancellingDelete,
  onToggleShopActive,
  onCancelDelete,
  error,
  confirm,
  isConfirmPending,
  onConfirm,
  onCloseConfirm,
}: SettingsDangerPanelProps) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const statusLabel = isShopActive ? t("danger.activeStatus") : t("danger.inactiveStatus");
  const ToggleIcon = isShopActive ? EyeSlash : Eye;

  return (
    <Box sx={styles.stack}>
      {error ? (
        <Box role="alert" sx={styles.errorNote}>
          {error}
        </Box>
      ) : null}

      <Box sx={styles.dangerCard}>
        <Box sx={styles.dangerHeader}>
          <ToggleIcon size={24} variant="Bold" color={theme.palette.error.main} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: { xs: "0.78125rem", md: "0.84375rem" }, fontWeight: 700 }}>
              {isMobile ? t("danger.mobileDeactivate") : t("danger.deactivate")}
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.6875rem", md: "0.75rem" }, color: "text.secondary", lineHeight: 1.6 }}>
              {isMobile
                ? t("danger.mobileDeactivateDescription", { status: statusLabel })
                : t("danger.deactivateDescription", { status: statusLabel })}
            </Typography>
          </Box>
          <Box component="span" sx={styles.statusChip(isShopActive)}>
            {statusLabel}
          </Box>
        </Box>
        <Box sx={styles.actionRow}>
          <SefanoButton
            intent={isShopActive ? "danger" : "primary"}
            disabled={isToggling}
            onClick={onToggleShopActive}
            sx={{ width: { xs: "100%", md: 190 } }}
          >
            {isShopActive ? t("danger.deactivateAction") : t("danger.reactivateAction")}
          </SefanoButton>
        </Box>
      </Box>

      <Box sx={styles.dangerCard}>
        <Box sx={styles.dangerHeader}>
          <Trash size={24} variant="Bold" color={theme.palette.error.main} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: { xs: "0.78125rem", md: "0.84375rem" }, fontWeight: 700 }}>
              {t("danger.delete")}
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.6875rem", md: "0.75rem" }, color: "text.secondary", lineHeight: 1.6 }}>
              {isMobile ? t("danger.mobileDeleteDescription") : t("danger.deleteDescription")}
            </Typography>
          </Box>
        </Box>

        {deleteRequested ? (
          <Box sx={styles.deleteRequested}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
              <Clock size={18} variant="Bold" color={theme.palette.error.main} />
              <Box component="span">{t("danger.deleteScheduledAt", { date: scheduledDeleteLabel })}</Box>
            </Box>
            <SefanoButton
              intent="danger"
              disabled={isCancellingDelete}
              onClick={onCancelDelete}
              sx={{ width: { xs: "100%", md: 190 } }}
            >
              {t("danger.cancelDeleteAction")}
            </SefanoButton>
          </Box>
        ) : (
          <Box component="form" onSubmit={onSubmit} sx={styles.deleteRow}>
            <Box sx={{ flex: 1, width: "100%" }}>
              <FloatingTextField
                label={isMobile ? t("danger.mobileConfirmName") : t("danger.confirmName")}
                placeholder={shopName}
                error={Boolean(form.formState.errors.confirmName)}
                helperText={form.formState.errors.confirmName?.message ?? t("danger.confirmNameHint", { name: shopName })}
                {...form.register("confirmName")}
              />
            </Box>
            <SefanoButton
              type="submit"
              intent="danger"
              disabled={!deleteMatches || isSchedulingDelete}
              sx={{ width: { xs: "100%", md: 160 }, height: 56 }}
            >
              {t("danger.deleteAction")}
            </SefanoButton>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm === "deactivate" ? t("danger.confirmDeactivateTitle") : t("danger.confirmDeleteTitle")}
        message={
          confirm === "deactivate"
            ? t("danger.confirmDeactivateMessage", { name: shopName })
            : t("danger.confirmDeleteMessage", { name: shopName })
        }
        confirmLabel={confirm === "deactivate" ? t("danger.deactivateAction") : t("danger.deleteAction")}
        cancelLabel={t("cancel")}
        isPending={isConfirmPending}
        onConfirm={onConfirm}
        onCancel={onCloseConfirm}
      />
    </Box>
  );
}
